import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { conciliacionAPI } from '../services/api';
import { useApp } from '../context/AppContext';

const ConciliacionBancaria = () => {
  const { formatCurrency } = useApp();

  // ─── Tab activo ───
  const [tab, setTab] = useState('conciliacion');

  // ─── Cuentas Bancarias ───
  const [cuentas, setCuentas] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(true);
  const [showCuentaModal, setShowCuentaModal] = useState(false);
  const [cuentaForm, setCuentaForm] = useState({
    id: null, nombre: '', banco: '', tipo_cuenta: 'corriente', numero_cuenta: '', saldo_actual: ''
  });

  // ─── Períodos ───
  const [periodos, setPeriodos] = useState([]);
  const [todosLosPeriodos, setTodosLosPeriodos] = useState([]);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState(null);
  const [periodoDetalle, setPeriodoDetalle] = useState(null);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [showNuevoPeriodo, setShowNuevoPeriodo] = useState(false);
  const [periodoForm, setPeriodoForm] = useState({
    cuenta_bancaria_id: '', mes: new Date().getMonth() + 1, anio: new Date().getFullYear(),
    saldo_inicial_banco: '', saldo_inicial_libros: ''
  });

  // ─── Líneas Estado de Cuenta ───
  const [lineas, setLineas] = useState([]);
  const [loadingLineas, setLoadingLineas] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [lineaSeleccionada, setLineaSeleccionada] = useState(null);

  // ─── Registros internos para matching ───
  const [registrosInternos, setRegistrosInternos] = useState([]);
  const [loadingRegistros, setLoadingRegistros] = useState(false);

  // ─── Importación ───
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // ─── Ajustes ───
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [ajusteForm, setAjusteForm] = useState({
    linea_estado_cuenta_id: null, tipo: 'cargo_bancario', concepto: '', monto: ''
  });

  // ─── Reglas ───
  const [reglas, setReglas] = useState([]);
  const [loadingReglas, setLoadingReglas] = useState(false);
  const [showReglaModal, setShowReglaModal] = useState(false);
  const [reglaForm, setReglaForm] = useState({
    id: null, nombre: '', descripcion: '', activa: true, prioridad: 10,
    condicion_descripcion: 'contiene', condicion_descripcion_valor: '',
    condicion_tipo: 'ambos', condicion_monto_min: '', condicion_monto_max: '',
    accion_tipo: 'crear_ajuste', accion_concepto: '', auto_conciliar: false
  });

  // ─── Resumen ───
  const [resumen, setResumen] = useState(null);

  // ─── Estado general ───
  const [saving, setSaving] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);

  // ═══════════════════════════════════════════
  // CARGA DE DATOS
  // ═══════════════════════════════════════════

  const cargarCuentas = useCallback(async () => {
    setLoadingCuentas(true);
    try {
      const res = await conciliacionAPI.getCuentas();
      if (res.data.success) setCuentas(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoadingCuentas(false); }
  }, []);

  const cargarPeriodos = useCallback(async () => {
    if (!cuentaSeleccionada) { setPeriodos([]); return; }
    setLoadingPeriodos(true);
    try {
      const res = await conciliacionAPI.getPeriodos({ cuenta_bancaria_id: cuentaSeleccionada });
      if (res.data.success) setPeriodos(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoadingPeriodos(false); }
  }, [cuentaSeleccionada]);

  const cargarDetallePeriodo = useCallback(async (periodoId) => {
    if (!periodoId) return;
    try {
      const res = await conciliacionAPI.getPeriodoDetalle(periodoId);
      if (res.data.success) setPeriodoDetalle(res.data.data);
    } catch (e) { console.error(e); }
  }, []);

  const cargarLineas = useCallback(async () => {
    if (!periodoActivo) { setLineas([]); return; }
    setLoadingLineas(true);
    try {
      const params = {};
      if (filtroEstado) params.estado_conciliacion = filtroEstado;
      if (filtroTipo) params.tipo = filtroTipo;
      const res = await conciliacionAPI.getLineas(periodoActivo, params);
      if (res.data.success) setLineas(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoadingLineas(false); }
  }, [periodoActivo, filtroEstado, filtroTipo]);

  const buscarRegistros = useCallback(async (linea) => {
    if (!linea || !periodoActivo) return;
    setLoadingRegistros(true);
    try {
      const res = await conciliacionAPI.buscarRegistros(periodoActivo, {
        monto: linea.monto, tipo: linea.tipo, fecha: linea.fecha
      });
      if (res.data.success) setRegistrosInternos(res.data.data);
    } catch (e) { console.error(e); setRegistrosInternos([]); }
    finally { setLoadingRegistros(false); }
  }, [periodoActivo]);

  const cargarReglas = useCallback(async () => {
    setLoadingReglas(true);
    try {
      const res = await conciliacionAPI.getReglas();
      if (res.data.success) setReglas(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoadingReglas(false); }
  }, []);

  const cargarTodosLosPeriodos = useCallback(async () => {
    try {
      const res = await conciliacionAPI.getPeriodos({});
      if (res.data.success) setTodosLosPeriodos(res.data.data);
    } catch (e) { console.error(e); }
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const res = await conciliacionAPI.getResumen();
      if (res.data.success) setResumen(res.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { cargarCuentas(); cargarResumen(); }, [cargarCuentas, cargarResumen]);
  useEffect(() => { cargarPeriodos(); }, [cargarPeriodos]);
  useEffect(() => { cargarLineas(); }, [cargarLineas]);
  useEffect(() => { if (periodoActivo) cargarDetallePeriodo(periodoActivo); }, [periodoActivo, cargarDetallePeriodo]);
  useEffect(() => { if (tab === 'reglas') cargarReglas(); }, [tab, cargarReglas]);
  useEffect(() => { if (tab === 'historial') { cargarTodosLosPeriodos(); cargarResumen(); } }, [tab, cargarTodosLosPeriodos, cargarResumen]);

  // ═══════════════════════════════════════════
  // ACCIONES: CUENTAS BANCARIAS
  // ═══════════════════════════════════════════

  const abrirNuevaCuenta = () => {
    setCuentaForm({ id: null, nombre: '', banco: '', tipo_cuenta: 'corriente', numero_cuenta: '', saldo_actual: '' });
    setShowCuentaModal(true);
  };

  const editarCuenta = (cuenta) => {
    setCuentaForm({
      id: cuenta.id, nombre: cuenta.nombre, banco: cuenta.banco,
      tipo_cuenta: cuenta.tipo_cuenta, numero_cuenta: cuenta.numero_cuenta,
      saldo_actual: cuenta.saldo_actual || ''
    });
    setShowCuentaModal(true);
  };

  const guardarCuenta = async () => {
    if (!cuentaForm.nombre || !cuentaForm.banco || !cuentaForm.numero_cuenta) {
      toast.error('Complete todos los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      if (cuentaForm.id) {
        await conciliacionAPI.updateCuenta(cuentaForm.id, cuentaForm);
        toast.success('Cuenta actualizada');
      } else {
        await conciliacionAPI.createCuenta(cuentaForm);
        toast.success('Cuenta creada');
      }
      setShowCuentaModal(false);
      cargarCuentas();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: PERÍODOS
  // ═══════════════════════════════════════════

  const abrirNuevoPeriodo = () => {
    setPeriodoForm({
      cuenta_bancaria_id: cuentaSeleccionada || '',
      mes: new Date().getMonth() + 1,
      anio: new Date().getFullYear(),
      saldo_inicial_banco: '', saldo_inicial_libros: ''
    });
    setShowNuevoPeriodo(true);
  };

  const crearPeriodo = async () => {
    if (!periodoForm.cuenta_bancaria_id) { toast.error('Seleccione una cuenta bancaria'); return; }
    setSaving(true);
    try {
      const res = await conciliacionAPI.crearPeriodo(periodoForm);
      if (res.data.success) {
        toast.success('Período creado');
        setShowNuevoPeriodo(false);
        cargarPeriodos();
        setPeriodoActivo(res.data.data.id);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al crear período'); }
    finally { setSaving(false); }
  };

  const seleccionarPeriodo = (id) => {
    setPeriodoActivo(id);
    setLineaSeleccionada(null);
    setRegistrosInternos([]);
  };

  const cerrarPeriodo = async () => {
    if (!periodoActivo) return;
    if (!window.confirm('¿Cerrar este período de conciliación? Esta acción no se puede deshacer.')) return;
    setSaving(true);
    try {
      const res = await conciliacionAPI.cerrarPeriodo(periodoActivo);
      if (res.data.success) {
        toast.success('Período cerrado exitosamente');
        cargarPeriodos();
        cargarDetallePeriodo(periodoActivo);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al cerrar período'); }
    finally { setSaving(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: IMPORTAR ESTADO DE CUENTA
  // ═══════════════════════════════════════════

  const importarArchivo = async () => {
    if (!importFile) { toast.error('Seleccione un archivo'); return; }
    setImporting(true);
    try {
      const res = await conciliacionAPI.importarEstadoCuenta(periodoActivo, importFile);
      if (res.data.success) {
        toast.success(`Importadas ${res.data.data.lineas_importadas} líneas del estado de cuenta`);
        setShowImportModal(false);
        setImportFile(null);
        cargarLineas();
        cargarDetallePeriodo(periodoActivo);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al importar archivo'); }
    finally { setImporting(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: AUTO-MATCH
  // ═══════════════════════════════════════════

  const ejecutarAutoMatch = async () => {
    if (!periodoActivo) return;
    setAutoMatching(true);
    try {
      const res = await conciliacionAPI.ejecutarAutoMatch(periodoActivo);
      if (res.data.success) {
        const r = res.data.data;
        toast.success(`Auto-match: ${r.matches_creados} coincidencias encontradas (${r.pendientes_restantes} pendientes)`);
        cargarLineas();
        cargarDetallePeriodo(periodoActivo);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error en auto-match'); }
    finally { setAutoMatching(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: MATCH MANUAL
  // ═══════════════════════════════════════════

  const seleccionarLinea = (linea) => {
    if (linea.estado_conciliacion === 'conciliado') {
      setLineaSeleccionada(linea);
      setRegistrosInternos([]);
      return;
    }
    setLineaSeleccionada(linea);
    buscarRegistros(linea);
  };

  const matchManual = async (registro) => {
    if (!lineaSeleccionada) return;
    setSaving(true);
    try {
      await conciliacionAPI.matchManual(lineaSeleccionada.id, {
        tipo_match: registro.tipo_registro, registro_id: registro.id
      });
      toast.success('Línea conciliada manualmente');
      cargarLineas();
      cargarDetallePeriodo(periodoActivo);
      setLineaSeleccionada(null);
      setRegistrosInternos([]);
    } catch (e) { toast.error(e.response?.data?.error || 'Error al emparejar'); }
    finally { setSaving(false); }
  };

  const deshacerMatch = async (lineaId) => {
    if (!window.confirm('¿Deshacer esta conciliación?')) return;
    setSaving(true);
    try {
      await conciliacionAPI.unmatch(lineaId);
      toast.success('Conciliación deshecha');
      cargarLineas();
      cargarDetallePeriodo(periodoActivo);
      setLineaSeleccionada(null);
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const ignorarLinea = async (lineaId) => {
    if (!window.confirm('¿Ignorar esta línea? (Ej: movimiento entre cuentas propias)')) return;
    setSaving(true);
    try {
      await conciliacionAPI.ignorarLinea(lineaId);
      toast.success('Línea marcada como ignorada');
      cargarLineas();
      cargarDetallePeriodo(periodoActivo);
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: AJUSTES
  // ═══════════════════════════════════════════

  const abrirAjuste = (linea = null) => {
    setAjusteForm({
      periodo_conciliacion_id: periodoActivo,
      linea_estado_cuenta_id: linea?.id || null,
      tipo: 'cargo_bancario',
      concepto: linea?.descripcion || '',
      monto: linea ? Math.abs(parseFloat(linea.monto)) : ''
    });
    setShowAjusteModal(true);
  };

  const guardarAjuste = async () => {
    if (!ajusteForm.concepto || !ajusteForm.monto) {
      toast.error('Complete concepto y monto');
      return;
    }
    setSaving(true);
    try {
      await conciliacionAPI.crearAjuste({
        ...ajusteForm,
        periodo_conciliacion_id: periodoActivo
      });
      toast.success('Ajuste creado');
      setShowAjusteModal(false);
      cargarLineas();
      cargarDetallePeriodo(periodoActivo);
    } catch (e) { toast.error(e.response?.data?.error || 'Error al crear ajuste'); }
    finally { setSaving(false); }
  };

  // ═══════════════════════════════════════════
  // ACCIONES: REGLAS
  // ═══════════════════════════════════════════

  const abrirNuevaRegla = () => {
    setReglaForm({
      id: null, nombre: '', descripcion: '', activa: true, prioridad: 10,
      condicion_descripcion: 'contiene', condicion_descripcion_valor: '',
      condicion_tipo: 'ambos', condicion_monto_min: '', condicion_monto_max: '',
      accion_tipo: 'crear_ajuste', accion_concepto: '', auto_conciliar: false
    });
    setShowReglaModal(true);
  };

  const editarRegla = (regla) => {
    setReglaForm({
      id: regla.id, nombre: regla.nombre, descripcion: regla.descripcion || '',
      activa: regla.activa, prioridad: regla.prioridad || 10,
      condicion_descripcion: regla.condicion_descripcion || 'contiene',
      condicion_descripcion_valor: regla.condicion_descripcion_valor || '',
      condicion_tipo: regla.condicion_tipo || 'ambos',
      condicion_monto_min: regla.condicion_monto_min || '',
      condicion_monto_max: regla.condicion_monto_max || '',
      accion_tipo: regla.accion_tipo, accion_concepto: regla.accion_concepto || '',
      auto_conciliar: regla.auto_conciliar || false
    });
    setShowReglaModal(true);
  };

  const guardarRegla = async () => {
    if (!reglaForm.nombre || !reglaForm.condicion_descripcion_valor || !reglaForm.accion_tipo) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      if (reglaForm.id) {
        await conciliacionAPI.updateRegla(reglaForm.id, reglaForm);
        toast.success('Regla actualizada');
      } else {
        await conciliacionAPI.createRegla(reglaForm);
        toast.success('Regla creada');
      }
      setShowReglaModal(false);
      cargarReglas();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar regla'); }
    finally { setSaving(false); }
  };

  const toggleRegla = async (regla) => {
    try {
      await conciliacionAPI.updateRegla(regla.id, { activa: !regla.activa });
      cargarReglas();
    } catch (e) { toast.error('Error al actualizar regla'); }
  };

  // ═══════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════

  const estadoBadge = (estado) => {
    const map = {
      pendiente: 'bg-warning text-dark',
      conciliado: 'bg-success',
      parcial: 'bg-info',
      excepcion: 'bg-danger',
      ignorado: 'bg-secondary'
    };
    return map[estado] || 'bg-secondary';
  };

  const estadoPeriodoBadge = (estado) => {
    const map = {
      borrador: 'bg-secondary',
      en_proceso: 'bg-primary',
      conciliado: 'bg-success',
      cerrado: 'bg-dark'
    };
    return map[estado] || 'bg-secondary';
  };

  const tipoRegistroLabel = (tipo) => {
    const map = {
      venta: 'Venta', abono: 'Abono CxC', gasto: 'Gasto/Egreso',
      transaccion_pago: 'Transacción Pago', ajuste: 'Ajuste',
      lote_tarjeta: 'Lote Tarjeta'
    };
    return map[tipo] || tipo;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha + 'T00:00:00').toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const porcentajeConciliado = periodoDetalle
    ? (periodoDetalle.lineas_total > 0 ? Math.round((periodoDetalle.lineas_conciliadas / periodoDetalle.lineas_total) * 100) : 0)
    : 0;

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div className="container-fluid">
      {/* TABS */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'conciliacion', icon: 'bi-bank2', label: 'Conciliación' },
          { key: 'cuentas', icon: 'bi-credit-card', label: 'Cuentas Bancarias' },
          { key: 'reglas', icon: 'bi-gear', label: 'Reglas' },
          { key: 'historial', icon: 'bi-clock-history', label: 'Historial' }
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <i className={`bi ${t.icon} me-1`}></i> {t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ═══════════════════════════════════════════ */}
      {/* TAB: CONCILIACIÓN (principal) */}
      {/* ═══════════════════════════════════════════ */}
      {tab === 'conciliacion' && (
        <div>
          {/* Selectores de Cuenta + Período */}
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Cuenta Bancaria</label>
              <select className="form-select" value={cuentaSeleccionada}
                onChange={(e) => { setCuentaSeleccionada(e.target.value); setPeriodoActivo(null); setPeriodoDetalle(null); setLineas([]); }}>
                <option value="">-- Seleccionar cuenta --</option>
                {cuentas.filter(c => c.activa).map(c => (
                  <option key={c.id} value={c.id}>{c.banco} - {c.numero_cuenta} ({c.tipo_cuenta})</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Período</label>
              <select className="form-select" value={periodoActivo || ''}
                onChange={(e) => seleccionarPeriodo(e.target.value ? parseInt(e.target.value) : null)}
                disabled={!cuentaSeleccionada}>
                <option value="">-- Seleccionar período --</option>
                {periodos.map(p => (
                  <option key={p.id} value={p.id}>{p.codigo} - {p.estado}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end gap-2">
              <button className="btn btn-primary" onClick={abrirNuevoPeriodo} disabled={!cuentaSeleccionada}>
                <i className="bi bi-plus-lg me-1"></i>Nuevo Período
              </button>
            </div>
          </div>

          {/* Cards Resumen del Período */}
          {periodoDetalle && (
            <>
              <div className="row mb-3 g-2">
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center p-3">
                      <div className="text-muted small">Saldo Banco</div>
                      <div className="fs-5 fw-bold text-primary">{formatCurrency(periodoDetalle.saldo_inicial_banco)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center p-3">
                      <div className="text-muted small">Saldo Libros</div>
                      <div className="fs-5 fw-bold text-info">{formatCurrency(periodoDetalle.saldo_inicial_libros)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center p-3">
                      <div className="text-muted small">Diferencia</div>
                      <div className={`fs-5 fw-bold ${parseFloat(periodoDetalle.diferencia || 0) === 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(periodoDetalle.diferencia || 0)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center p-3">
                      <div className="text-muted small">Conciliado</div>
                      <div className="fs-5 fw-bold text-success">{porcentajeConciliado}%</div>
                      <div className="progress mt-1" style={{ height: '6px' }}>
                        <div className="progress-bar bg-success" style={{ width: `${porcentajeConciliado}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="d-flex gap-2 mb-3 flex-wrap">
                <button className="btn btn-outline-primary" onClick={() => setShowImportModal(true)}
                  disabled={periodoDetalle.estado === 'cerrado'}>
                  <i className="bi bi-upload me-1"></i>Importar Estado de Cuenta
                </button>
                <button className="btn btn-outline-success" onClick={ejecutarAutoMatch}
                  disabled={autoMatching || periodoDetalle.estado === 'cerrado' || lineas.length === 0}>
                  {autoMatching ? (
                    <><span className="spinner-border spinner-border-sm me-1"></span>Procesando...</>
                  ) : (
                    <><i className="bi bi-magic me-1"></i>Auto-Match</>
                  )}
                </button>
                <button className="btn btn-outline-warning" onClick={() => abrirAjuste()}
                  disabled={periodoDetalle.estado === 'cerrado'}>
                  <i className="bi bi-pencil-square me-1"></i>Nuevo Ajuste
                </button>
                {periodoDetalle.estado !== 'cerrado' && (
                  <button className="btn btn-dark ms-auto" onClick={cerrarPeriodo} disabled={saving}>
                    <i className="bi bi-lock me-1"></i>Cerrar Período
                  </button>
                )}
              </div>
            </>
          )}

          {/* Vista dividida: Líneas Banco | Registros Internos */}
          {periodoActivo && (
            <div className="row g-3">
              {/* Izquierda: Líneas del estado de cuenta */}
              <div className={lineaSeleccionada && lineaSeleccionada.estado_conciliacion !== 'conciliado' ? 'col-md-7' : 'col-12'}>
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h6 className="mb-0"><i className="bi bi-bank me-1"></i>Estado de Cuenta Bancario ({lineas.length} líneas)</h6>
                    <div className="d-flex gap-2">
                      <select className="form-select form-select-sm" style={{ width: 'auto' }}
                        value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos los estados</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="conciliado">Conciliado</option>
                        <option value="excepcion">Excepción</option>
                        <option value="ignorado">Ignorado</option>
                      </select>
                      <select className="form-select form-select-sm" style={{ width: 'auto' }}
                        value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="credito">Créditos</option>
                        <option value="debito">Débitos</option>
                      </select>
                    </div>
                  </div>
                  <div className="card-body p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {loadingLineas ? (
                      <div className="text-center py-4"><span className="spinner-border spinner-border-sm"></span></div>
                    ) : lineas.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-inbox display-6 d-block mb-2"></i>
                        {periodoDetalle ? 'No hay líneas. Importe un estado de cuenta.' : 'Seleccione un período'}
                      </div>
                    ) : (
                      <table className="table table-hover table-sm mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: '90px' }}>Fecha</th>
                            <th>Descripción</th>
                            <th className="text-end" style={{ width: '110px' }}>Monto</th>
                            <th style={{ width: '100px' }}>Estado</th>
                            <th style={{ width: '60px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineas.map(linea => (
                            <tr key={linea.id}
                              className={`cursor-pointer ${lineaSeleccionada?.id === linea.id ? 'table-active' : ''}`}
                              onClick={() => seleccionarLinea(linea)}
                              style={{ cursor: 'pointer' }}>
                              <td className="small">{formatFecha(linea.fecha)}</td>
                              <td>
                                <div className="small fw-medium text-truncate" style={{ maxWidth: '300px' }}>{linea.descripcion}</div>
                                {linea.referencia && <small className="text-muted">Ref: {linea.referencia}</small>}
                              </td>
                              <td className={`text-end fw-semibold ${linea.tipo === 'credito' ? 'text-success' : 'text-danger'}`}>
                                {linea.tipo === 'credito' ? '+' : '-'}{formatCurrency(Math.abs(parseFloat(linea.monto)))}
                              </td>
                              <td>
                                <span className={`badge ${estadoBadge(linea.estado_conciliacion)} rounded-pill`}>
                                  {linea.estado_conciliacion}
                                </span>
                                {linea.match_automatico && <i className="bi bi-robot ms-1 text-info small" title="Auto-match"></i>}
                              </td>
                              <td>
                                {linea.estado_conciliacion === 'conciliado' && periodoDetalle?.estado !== 'cerrado' && (
                                  <button className="btn btn-outline-secondary btn-sm py-0 px-1"
                                    onClick={(e) => { e.stopPropagation(); deshacerMatch(linea.id); }}
                                    title="Deshacer match">
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                  </button>
                                )}
                                {linea.estado_conciliacion === 'pendiente' && periodoDetalle?.estado !== 'cerrado' && (
                                  <div className="d-flex gap-1">
                                    <button className="btn btn-outline-secondary btn-sm py-0 px-1"
                                      onClick={(e) => { e.stopPropagation(); ignorarLinea(linea.id); }}
                                      title="Ignorar línea">
                                      <i className="bi bi-eye-slash"></i>
                                    </button>
                                    <button className="btn btn-outline-warning btn-sm py-0 px-1"
                                      onClick={(e) => { e.stopPropagation(); abrirAjuste(linea); }}
                                      title="Crear ajuste">
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {/* Derecha: Registros internos sugeridos */}
              {lineaSeleccionada && lineaSeleccionada.estado_conciliacion !== 'conciliado' && (
                <div className="col-md-5">
                  <div className="card border-0 shadow-sm border-start border-primary border-3">
                    <div className="card-header bg-white">
                      <h6 className="mb-0"><i className="bi bi-link-45deg me-1"></i>Registros para Emparejar</h6>
                      <small className="text-muted">
                        Línea: {lineaSeleccionada.descripcion?.substring(0, 50)}... |
                        <strong className={lineaSeleccionada.tipo === 'credito' ? 'text-success' : 'text-danger'}>
                          {' '}{formatCurrency(Math.abs(parseFloat(lineaSeleccionada.monto)))}
                        </strong>
                      </small>
                    </div>
                    <div className="card-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {loadingRegistros ? (
                        <div className="text-center py-4"><span className="spinner-border spinner-border-sm"></span></div>
                      ) : registrosInternos.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="bi bi-search display-6 d-block mb-2"></i>
                          No se encontraron registros coincidentes
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {registrosInternos.map((reg, idx) => (
                            <div key={`${reg.tipo_registro}-${reg.id}-${idx}`}
                              className="list-group-item list-group-item-action">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="small fw-medium">{reg.cliente || reg.concepto || reg.proveedor || reg.codigo}</div>
                                  <span className="badge bg-light text-dark me-1">{tipoRegistroLabel(reg.tipo_registro)}</span>
                                  <small className="text-muted">{formatFecha(reg.created_at?.split('T')[0])}</small>
                                  {reg.codigo && <small className="text-muted ms-1">| {reg.codigo}</small>}
                                </div>
                                <div className="text-end">
                                  <div className="fw-bold">{formatCurrency(parseFloat(reg.monto))}</div>
                                  <button className="btn btn-sm btn-success mt-1"
                                    onClick={() => matchManual(reg)} disabled={saving}>
                                    <i className="bi bi-check-lg me-1"></i>Emparejar
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Estado vacío */}
          {!cuentaSeleccionada && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-bank2 display-1 d-block mb-3"></i>
              <h5>Conciliación Bancaria</h5>
              <p>Seleccione una cuenta bancaria para comenzar</p>
              {cuentas.length === 0 && (
                <button className="btn btn-primary" onClick={() => setTab('cuentas')}>
                  <i className="bi bi-plus-lg me-1"></i>Crear primera cuenta bancaria
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* TAB: CUENTAS BANCARIAS */}
      {/* ═══════════════════════════════════════════ */}
      {tab === 'cuentas' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Cuentas Bancarias</h5>
            <button className="btn btn-primary" onClick={abrirNuevaCuenta}>
              <i className="bi bi-plus-lg me-1"></i>Nueva Cuenta
            </button>
          </div>

          {loadingCuentas ? (
            <div className="text-center py-4"><span className="spinner-border"></span></div>
          ) : cuentas.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-credit-card display-1 d-block mb-3"></i>
              <h5>No hay cuentas bancarias registradas</h5>
              <p>Agregue su primera cuenta bancaria para iniciar la conciliación</p>
            </div>
          ) : (
            <div className="row g-3">
              {cuentas.map(cuenta => (
                <div key={cuenta.id} className="col-md-6 col-lg-4">
                  <div className={`card border-0 shadow-sm h-100 ${!cuenta.activa ? 'opacity-50' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-0">{cuenta.nombre}</h6>
                          <small className="text-muted">{cuenta.banco}</small>
                        </div>
                        <span className={`badge ${cuenta.activa ? 'bg-success' : 'bg-secondary'}`}>
                          {cuenta.activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                      <div className="mb-2">
                        <i className="bi bi-credit-card me-1 text-muted"></i>
                        <span className="small">{cuenta.tipo_cuenta} - {cuenta.numero_cuenta}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted small">Saldo:</span>
                        <span className="fw-bold ms-1">{formatCurrency(cuenta.saldo_actual || 0)}</span>
                      </div>
                      {cuenta.sucursal_nombre && (
                        <div className="small text-muted">
                          <i className="bi bi-shop me-1"></i>{cuenta.sucursal_nombre}
                        </div>
                      )}
                      <div className="mt-3">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => editarCuenta(cuenta)}>
                          <i className="bi bi-pencil me-1"></i>Editar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* TAB: REGLAS */}
      {/* ═══════════════════════════════════════════ */}
      {tab === 'reglas' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Reglas de Conciliación Automática</h5>
            <button className="btn btn-primary" onClick={abrirNuevaRegla}>
              <i className="bi bi-plus-lg me-1"></i>Nueva Regla
            </button>
          </div>

          <div className="alert alert-info small">
            <i className="bi bi-info-circle me-1"></i>
            Las reglas se aplican durante el Auto-Match para identificar automáticamente cargos bancarios recurrentes
            (mantenimiento, comisiones, etc.) y crear ajustes correspondientes.
          </div>

          {loadingReglas ? (
            <div className="text-center py-4"><span className="spinner-border"></span></div>
          ) : reglas.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-gear display-1 d-block mb-3"></i>
              <h5>No hay reglas configuradas</h5>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Regla</th>
                    <th>Condición</th>
                    <th>Tipo</th>
                    <th>Acción</th>
                    <th>Auto</th>
                    <th>Prioridad</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map(regla => (
                    <tr key={regla.id} className={!regla.activa ? 'opacity-50' : ''}>
                      <td>
                        <div className="fw-medium">{regla.nombre}</div>
                        {regla.descripcion && <small className="text-muted">{regla.descripcion}</small>}
                      </td>
                      <td className="small">
                        Descripción <strong>{regla.condicion_descripcion}</strong> "{regla.condicion_descripcion_valor}"
                      </td>
                      <td><span className="badge bg-light text-dark">{regla.condicion_tipo}</span></td>
                      <td className="small">{regla.accion_tipo === 'crear_ajuste' ? 'Crear ajuste' : 'Ignorar'}</td>
                      <td>
                        {regla.auto_conciliar
                          ? <i className="bi bi-check-circle-fill text-success"></i>
                          : <i className="bi bi-x-circle text-muted"></i>}
                      </td>
                      <td>{regla.prioridad}</td>
                      <td>
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" checked={regla.activa}
                            onChange={() => toggleRegla(regla)} />
                        </div>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => editarRegla(regla)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* TAB: HISTORIAL */}
      {/* ═══════════════════════════════════════════ */}
      {tab === 'historial' && (
        <div>
          <h5 className="mb-3">Historial de Conciliaciones</h5>

          {/* Resumen general */}
          {resumen && Array.isArray(resumen) && resumen.length > 0 && (
            <div className="row mb-4 g-2">
              {resumen.map(c => (
                <div key={c.id} className="col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="mb-1">{c.banco} - {c.numero_cuenta}</h6>
                      <div className="mb-1"><span className="text-muted small">Saldo:</span> <span className="fw-bold">{formatCurrency(c.saldo_actual)}</span></div>
                      {c.ultimo_periodo && (
                        <div className="small text-muted">
                          Último período: <span className="badge bg-light text-dark">{c.ultimo_periodo.codigo}</span>
                          <span className={`badge ms-1 ${estadoPeriodoBadge(c.ultimo_periodo.estado)}`}>{c.ultimo_periodo.estado}</span>
                          {c.ultimo_periodo.lineas_total > 0 && (
                            <span className="ms-1">({c.ultimo_periodo.lineas_conciliadas}/{c.ultimo_periodo.lineas_total} líneas)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Lista de todos los períodos */}
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Código</th>
                  <th>Cuenta</th>
                  <th>Período</th>
                  <th className="text-end">Saldo Banco</th>
                  <th className="text-center">Líneas</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {todosLosPeriodos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No hay períodos de conciliación creados aún
                    </td>
                  </tr>
                ) : todosLosPeriodos.map(p => (
                  <tr key={p.id}>
                    <td className="fw-medium">{p.codigo}</td>
                    <td className="small">{p.banco} - {p.numero_cuenta}</td>
                    <td className="small">{formatFecha(p.fecha_inicio)} - {formatFecha(p.fecha_fin)}</td>
                    <td className="text-end">{formatCurrency(p.saldo_inicial_banco)}</td>
                    <td className="text-center">
                      <span className="text-success">{p.lineas_conciliadas || 0}</span>
                      <span className="text-muted">/{p.lineas_total || 0}</span>
                    </td>
                    <td><span className={`badge ${estadoPeriodoBadge(p.estado)}`}>{p.estado}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setCuentaSeleccionada(p.cuenta_bancaria_id?.toString() || '');
                          setPeriodoActivo(p.id);
                          setTab('conciliacion');
                        }}>
                        <i className="bi bi-eye me-1"></i>Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* MODALES */}
      {/* ═══════════════════════════════════════════ */}

      {/* Modal: Cuenta Bancaria */}
      {showCuentaModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowCuentaModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-credit-card me-2"></i>
                    {cuentaForm.id ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowCuentaModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Nombre de la Cuenta *</label>
                    <input type="text" className="form-control" placeholder="Ej: Cuenta Principal Pichincha"
                      value={cuentaForm.nombre} onChange={(e) => setCuentaForm({ ...cuentaForm, nombre: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Banco *</label>
                    <select className="form-select" value={cuentaForm.banco}
                      onChange={(e) => setCuentaForm({ ...cuentaForm, banco: e.target.value })}>
                      <option value="">-- Seleccionar --</option>
                      <option value="Banco Pichincha">Banco Pichincha</option>
                      <option value="Banco del Pacífico">Banco del Pacífico</option>
                      <option value="Produbanco">Produbanco</option>
                      <option value="Banco de Guayaquil">Banco de Guayaquil</option>
                      <option value="Banco Bolivariano">Banco Bolivariano</option>
                      <option value="Banco Internacional">Banco Internacional</option>
                      <option value="Banco del Austro">Banco del Austro</option>
                      <option value="Cooperativa JEP">Cooperativa JEP</option>
                      <option value="BanEcuador">BanEcuador</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Tipo de Cuenta</label>
                      <select className="form-select" value={cuentaForm.tipo_cuenta}
                        onChange={(e) => setCuentaForm({ ...cuentaForm, tipo_cuenta: e.target.value })}>
                        <option value="corriente">Corriente</option>
                        <option value="ahorros">Ahorros</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Número de Cuenta *</label>
                      <input type="text" className="form-control" placeholder="Ej: 2100123456"
                        value={cuentaForm.numero_cuenta} onChange={(e) => setCuentaForm({ ...cuentaForm, numero_cuenta: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Saldo Actual</label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={cuentaForm.saldo_actual} onChange={(e) => setCuentaForm({ ...cuentaForm, saldo_actual: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowCuentaModal(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={guardarCuenta} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> : <><i className="bi bi-check-lg me-1"></i>Guardar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Nuevo Período */}
      {showNuevoPeriodo && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowNuevoPeriodo(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="bi bi-calendar-plus me-2"></i>Nuevo Período de Conciliación</h5>
                  <button type="button" className="btn-close" onClick={() => setShowNuevoPeriodo(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Cuenta Bancaria *</label>
                    <select className="form-select" value={periodoForm.cuenta_bancaria_id}
                      onChange={(e) => setPeriodoForm({ ...periodoForm, cuenta_bancaria_id: e.target.value })}>
                      <option value="">-- Seleccionar --</option>
                      {cuentas.filter(c => c.activa).map(c => (
                        <option key={c.id} value={c.id}>{c.banco} - {c.numero_cuenta}</option>
                      ))}
                    </select>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Mes</label>
                      <select className="form-select" value={periodoForm.mes}
                        onChange={(e) => setPeriodoForm({ ...periodoForm, mes: parseInt(e.target.value) })}>
                        {[
                          'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Año</label>
                      <input type="number" className="form-control" value={periodoForm.anio}
                        onChange={(e) => setPeriodoForm({ ...periodoForm, anio: parseInt(e.target.value) })} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Saldo Inicial Banco</label>
                      <input type="number" step="0.01" className="form-control" placeholder="Del estado de cuenta"
                        value={periodoForm.saldo_inicial_banco}
                        onChange={(e) => setPeriodoForm({ ...periodoForm, saldo_inicial_banco: e.target.value })} />
                      <small className="text-muted">Se auto-calcula si hay período anterior</small>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Saldo Inicial Libros</label>
                      <input type="number" step="0.01" className="form-control" placeholder="De sus registros"
                        value={periodoForm.saldo_inicial_libros}
                        onChange={(e) => setPeriodoForm({ ...periodoForm, saldo_inicial_libros: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowNuevoPeriodo(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={crearPeriodo} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Creando...</> : <><i className="bi bi-check-lg me-1"></i>Crear Período</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Importar Estado de Cuenta */}
      {showImportModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowImportModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="bi bi-upload me-2"></i>Importar Estado de Cuenta</h5>
                  <button type="button" className="btn-close" onClick={() => setShowImportModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-info small">
                    <i className="bi bi-info-circle me-1"></i>
                    Suba el archivo Excel (.xlsx) del estado de cuenta descargado de su banco.
                    El sistema detecta automáticamente el formato de los bancos ecuatorianos principales.
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Archivo Excel / CSV</label>
                    <input ref={fileRef} type="file" className="form-control"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setImportFile(e.target.files[0])} />
                  </div>
                  {importFile && (
                    <div className="small text-muted">
                      <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                      {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => { setShowImportModal(false); setImportFile(null); }} disabled={importing}>Cancelar</button>
                  <button className="btn btn-primary" onClick={importarArchivo} disabled={importing || !importFile}>
                    {importing ? <><span className="spinner-border spinner-border-sm me-1"></span>Importando...</> : <><i className="bi bi-upload me-1"></i>Importar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Crear Ajuste */}
      {showAjusteModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowAjusteModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="bi bi-pencil-square me-2"></i>Crear Ajuste de Conciliación</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAjusteModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Tipo de Ajuste *</label>
                    <select className="form-select" value={ajusteForm.tipo}
                      onChange={(e) => setAjusteForm({ ...ajusteForm, tipo: e.target.value })}>
                      <option value="cargo_bancario">Cargo Bancario</option>
                      <option value="comision_tarjeta">Comisión por Tarjeta</option>
                      <option value="interes">Interés Bancario</option>
                      <option value="error">Error / Corrección</option>
                      <option value="redondeo">Redondeo</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Concepto *</label>
                    <input type="text" className="form-control" placeholder="Descripción del ajuste"
                      value={ajusteForm.concepto} onChange={(e) => setAjusteForm({ ...ajusteForm, concepto: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Monto *</label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={ajusteForm.monto} onChange={(e) => setAjusteForm({ ...ajusteForm, monto: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowAjusteModal(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-warning" onClick={guardarAjuste} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> : <><i className="bi bi-check-lg me-1"></i>Crear Ajuste</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Regla de Conciliación */}
      {showReglaModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowReglaModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="bi bi-gear me-2"></i>{reglaForm.id ? 'Editar Regla' : 'Nueva Regla'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowReglaModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-8">
                      <label className="form-label small fw-semibold">Nombre de la Regla *</label>
                      <input type="text" className="form-control" placeholder="Ej: Mantenimiento de Cuenta"
                        value={reglaForm.nombre} onChange={(e) => setReglaForm({ ...reglaForm, nombre: e.target.value })} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Prioridad</label>
                      <input type="number" className="form-control" value={reglaForm.prioridad}
                        onChange={(e) => setReglaForm({ ...reglaForm, prioridad: parseInt(e.target.value) })} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Descripción</label>
                    <input type="text" className="form-control" placeholder="Descripción opcional"
                      value={reglaForm.descripcion} onChange={(e) => setReglaForm({ ...reglaForm, descripcion: e.target.value })} />
                  </div>

                  <h6 className="mt-3 mb-2">Condición</h6>
                  <div className="row mb-3 g-2">
                    <div className="col-md-3">
                      <label className="form-label small">Descripción</label>
                      <select className="form-select form-select-sm" value={reglaForm.condicion_descripcion}
                        onChange={(e) => setReglaForm({ ...reglaForm, condicion_descripcion: e.target.value })}>
                        <option value="contiene">Contiene</option>
                        <option value="comienza_con">Comienza con</option>
                        <option value="igual">Es igual a</option>
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small">Valor a buscar *</label>
                      <input type="text" className="form-control form-control-sm" placeholder="Ej: MANTENIMIENTO"
                        value={reglaForm.condicion_descripcion_valor}
                        onChange={(e) => setReglaForm({ ...reglaForm, condicion_descripcion_valor: e.target.value })} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">Tipo Movimiento</label>
                      <select className="form-select form-select-sm" value={reglaForm.condicion_tipo}
                        onChange={(e) => setReglaForm({ ...reglaForm, condicion_tipo: e.target.value })}>
                        <option value="ambos">Ambos (Crédito y Débito)</option>
                        <option value="credito">Solo Créditos</option>
                        <option value="debito">Solo Débitos</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3 g-2">
                    <div className="col-md-6">
                      <label className="form-label small">Monto Mínimo</label>
                      <input type="number" step="0.01" className="form-control form-control-sm" placeholder="Sin límite"
                        value={reglaForm.condicion_monto_min}
                        onChange={(e) => setReglaForm({ ...reglaForm, condicion_monto_min: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Monto Máximo</label>
                      <input type="number" step="0.01" className="form-control form-control-sm" placeholder="Sin límite"
                        value={reglaForm.condicion_monto_max}
                        onChange={(e) => setReglaForm({ ...reglaForm, condicion_monto_max: e.target.value })} />
                    </div>
                  </div>

                  <h6 className="mt-3 mb-2">Acción</h6>
                  <div className="row mb-3 g-2">
                    <div className="col-md-4">
                      <label className="form-label small">Tipo de Acción *</label>
                      <select className="form-select form-select-sm" value={reglaForm.accion_tipo}
                        onChange={(e) => setReglaForm({ ...reglaForm, accion_tipo: e.target.value })}>
                        <option value="crear_ajuste">Crear Ajuste</option>
                        <option value="ignorar">Ignorar Línea</option>
                      </select>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label small">Concepto del Ajuste</label>
                      <input type="text" className="form-control form-control-sm" placeholder="Ej: Cargo bancario - Mantenimiento"
                        value={reglaForm.accion_concepto}
                        onChange={(e) => setReglaForm({ ...reglaForm, accion_concepto: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" checked={reglaForm.auto_conciliar}
                      onChange={(e) => setReglaForm({ ...reglaForm, auto_conciliar: e.target.checked })} />
                    <label className="form-check-label small">
                      Auto-conciliar (marca automáticamente como conciliado al aplicar)
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowReglaModal(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={guardarRegla} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> : <><i className="bi bi-check-lg me-1"></i>Guardar Regla</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConciliacionBancaria;
